/**
 * C3 — the drawing viewer (`KLQ-20260820-001` phase 3).
 *
 * The screen the product exists for. It assembles four things and owns none of them: the
 * rendition's state machine (`queries.ts`), the canvas (`ViewerCanvas`), the conversation
 * (`features/comments`, written in phase 1 and mounted here for the second time), and the
 * history (C1a's query, not a second one).
 *
 * WHAT IS NOT HERE, AND WHY IT IS ABSENT RATHER THAN DISABLED
 *
 * - **Compare.** `KLQ-20260820-002`, and none of its backend exists. The design's disabled
 *   state carries the tooltip *"Publish another version to compare"*, which would be a lie
 *   about why it is unavailable.
 * - **Review mode.** `MCK-20260521-007` owns the review surface and none of it is built, so
 *   the mode switcher has two segments rather than three: View and Comment.
 * - **Tasks and Activity tabs.** `MCK-20260521-008` is unbuilt; the annotations service refuses
 *   `task` and `issue` pins with a `409`. The invariant `features/projects/tabs.ts` already
 *   holds is the same one: a tab that cannot work is not rendered.
 * - **The left tool rail, outside Comment mode.** It exists only while a press can mark the
 *   sheet — Comment mode, on the current version, for a caller who may write (issue #71). Its
 *   84px column is not reserved otherwise: an empty strip reads as a broken layout.
 *
 * **Placing a pin is one request.** A press in Comment mode chooses a point; the composer over
 * it carries the words; `POST /projects/{id}/pins` writes the pin *and* its first comment
 * together, which is what `PinCreateRequest.body` exists for. Nothing is written before the
 * send, so an abandoned pin leaves no row anywhere — and no unexplained marker is ever visible
 * to the project.
 *
 * **The byte path.** Nothing here requests the `rendition_pdf` blob. That is the whole point of
 * the pyramid (ADR-0006) and of keeping the API out of the byte path (ADR-0005). Downloads are
 * a deliberate action on a named file, and they are the only place the original is touched.
 */

import { Link } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  History,
  MapPin,
  MessageSquare,
} from "lucide-react";
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { InlineError, Pill, Select } from "@/components";
import {
  CommentThread,
  useComments,
  type PinAnnotations,
} from "@/features/comments";
import { apiErrorKey, readDownloadUrl } from "@/features/drawings";
import { membersQueryOptions } from "@/features/people";
import {
  accessTo,
  relativeTime,
  useProjectSurfaces,
} from "@/features/projects";
import { useSlotTasks } from "@/features/tasks";
import { useLocale, useMessages } from "@/lib/i18n";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import {
  signTiles,
  type Markup,
  type Version,
  type TileBatch,
  type TileRect,
} from "./api";
import { MarkupLayer, type SendingStroke } from "./MarkupLayer";
import { MarkupTextComposer } from "./MarkupTextComposer";
import {
  MARKUP_PALETTE,
  resolveColor,
  type MarkupGeometry,
  type MarkupTool,
  type TextGeometry,
} from "./markup";
import {
  forgetDrawn,
  popEntry,
  recordErased,
  useMarkupSession,
} from "./markupSession";
import { PinComposer } from "./PinComposer";
import { PinLayer, type PinMarker } from "./PinLayer";
import { TaskPinLayer, type TaskPinMarker } from "./TaskPinLayer";
import { RenditionState } from "./RenditionState";
import { ToolRail, type MarkupColor, type ViewerLayers } from "./ToolRail";
import { VersionsRail } from "./VersionsRail";
import styles from "./Viewer.module.css";
import { sheetAnchor, type SheetAnchor, type SheetTransform } from "./overlay";
import { markMount } from "./timing";
import {
  renditionQueryOptions,
  useCreateMarkup,
  useCreatePin,
  useDeleteMarkup,
  useHistory,
  useMarkups,
  usePins,
  useRequestRendition,
  useRestoreMarkups,
} from "./queries";

/** How a version change reaches the address bar. Both halves default to the person's own move. */
export interface OpenVersionOptions {
  /**
   * Replace rather than push. Used only when the *link* named the version — resolving
   * `?comment=` to the pin's own sheet is not a navigation the person made, and leaving it in
   * the history would make Back a no-op on the screen they just arrived at.
   */
  readonly replace?: boolean;
  /** Keep `?comment=`. The deep link is still being resolved and its target has not been reached. */
  readonly keepHighlight?: boolean;
}

export interface ViewerPageProps {
  readonly projectId: string;
  readonly slotId: string;
  /** `?rev=` — an explicit version. Absent means the slot's current one. */
  readonly versionId: string | null;
  /** `?comment=` — scroll to and highlight one card, the phase-1 deep-link contract. */
  readonly highlightCommentId: string | null;
  readonly onOpenVersion: (
    versionId: string | null,
    options?: OpenVersionOptions,
  ) => void;
}

/**
 * OpenSeadragon is ~350 kB and this is the only screen that has ever needed it — so it is not
 * in the main bundle, where it would be paid for on the sign-in page.
 *
 * Split at the *canvas* rather than at the route, deliberately: a drawing whose pyramid is
 * still building renders a processing state and never downloads the viewer at all. On a site
 * tablet that is the difference between waiting for a render and waiting for a render plus a
 * third of a megabyte of JavaScript that has nothing to draw yet.
 */
const ViewerCanvas = lazy(async () => {
  const module = await import("./ViewerCanvas");
  return { default: module.ViewerCanvas };
});

type RailTab = "comments" | "versions";

/**
 * The two segments of wireframe 2's mode switcher — **two**, not three.
 *
 * Review is `MCK-20260521-007` and none of it is built, so it is absent rather than disabled.
 * View reads; Comment places pins and, from phase 5, draws markup.
 */
type Mode = "view" | "comment";

/** The blob whose pyramid this screen renders. Never the source attachments. */
function renditionBlobOf(version: Version | null): string | null {
  return (
    version?.files?.find((file) => file.kind === "rendition_pdf")
      ?.blob_ref_id ?? null
  );
}

export function ViewerPage({
  projectId,
  slotId,
  versionId,
  highlightCommentId,
  onOpenVersion,
}: ViewerPageProps) {
  const t = useMessages("drawings");
  const { locale } = useLocale();

  /**
   * The clock the phase-2 note's time-to-first-tile budget is measured against
   * (`docs/notes/2026-08-20-viewer-canvas.md` §10). Keyed on the slot, so opening a second
   * drawing without leaving this screen restarts the measurement rather than timing the second
   * sheet from the first one's mount.
   *
   * During render on purpose: *"measured from route mount"* is what the budget says, and an
   * effect runs after paint, which would quietly discount the first frame from every number.
   */
  markMount(slotId);

  const [tab, setTab] = useState<RailTab>("comments");
  /**
   * Whether the rail is open **on a phone** (wireframe 5a).
   *
   * Under 768px the sheet is full-bleed and the rail is a bottom sheet the action bar opens;
   * from 768px up the rail is a column and this flag is not read at all, because the rule that
   * hides it lives inside the phone's media query. A CSS class rather than a viewport hook,
   * for the reason the tablet band already gives: a `matchMedia` listener here would be a
   * second source of truth about the width, free to disagree with the stylesheet.
   */
  const [phoneRailOpen, setPhoneRailOpen] = useState(false);
  /**
   * Whether the tool rail is slid out **on a phone**.
   *
   * Closed by default and by the same reasoning as the rail beside it: the sheet is what a
   * person opened this screen for, and on 390px a permanent 84px of chrome is a fifth of it.
   * Above 768px the dock is `display: contents` and this flag is not read — the rail is a grid
   * column there, as it always was.
   */
  const [phoneToolsOpen, setPhoneToolsOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [mode, setMode] = useState<Mode>("view");
  // The rail's three pieces of state (issue #71). `ToolRail` holds none of them; the decisions
  // that tie them to pins and to the mode are this screen's.
  const [tool, setTool] = useState<MarkupTool | null>(null);
  const [color, setColor] = useState<MarkupColor>(MARKUP_PALETTE[0]);
  // **Rendering switches, never query keys.** Both lists stay cached whichever way these are
  // set; a toggle only mounts or unmounts a layer, so it can neither refetch nor ask the server
  // a question it was not asked.
  const [layers, setLayers] = useState<ViewerLayers>({
    pins: true,
    markup: true,
    tasks: true,
  });

  // The shell has already loaded the surface matrix, so this is a cache read rather than a
  // request. This is authority; whether a pin may be placed on *this sheet* is `canComment`
  // below, which is a fact about the version rather than about the person.
  const surfaces = useProjectSurfaces(projectId);
  const canWrite = accessTo(surfaces.data, "drawings") === "write";

  const history = useHistory(projectId, slotId);
  const versions = useMemo(() => history.data?.items ?? [], [history.data]);

  // `?rev=` names a version; its absence means the current one. Resolved from the history
  // rather than by a second request — the list is already here, and asking the server which
  // version is current when we are holding the answer is the N+1 C1a spent a whole endpoint
  // avoiding.
  const currentVersion =
    versions.find((version) => version.is_current) ?? null;
  const wantedId = versionId ?? currentVersion?.id ?? null;

  // A version id that belongs to another slot must not render a viewer showing the wrong
  // sheet. The history is this slot's, so a `?rev=` not in it is refused here rather than
  // fetched and then discovered to be foreign.
  const belongsToSlot =
    versionId === null ||
    versions.some((version) => version.id === versionId);

  /**
   * The version on screen, **read off the history rather than fetched by id.**
   *
   * `GET /projects/{id}/slots/{slot}/versions` answers with full `VersionResponse` rows,
   * `files[]` included (`versions/router.py:list_versions` builds every row through the same
   * `_response(version, files)` the single read uses). The `GET /versions/{id}` this screen
   * used to make "for `files`" was therefore the middle third of the open waterfall — history →
   * version → rendition → tile signing, ~0.6–1 s before the first pixel on a site tablet over
   * 4G (review 2026-09-25 §3.19). The rendition query now starts the moment the history lands,
   * one round trip earlier, and the id-keyed query stays in `queries.ts` for the one caller
   * that has no history in hand (`TaskDetail`'s thumbnail).
   *
   * A `?rev=` outside this slot resolves to `null` here (`belongsToSlot`), exactly as the fetch
   * by id was refused before; a `?rev=` naming a version the history does not hold yet is the
   * same `null` until the focus refetch brings it, which is the superseded banner's own path.
   */
  const openVersion =
    belongsToSlot && wantedId !== null
      ? (versions.find((version) => version.id === wantedId) ?? null)
      : null;
  const blobRefId = renditionBlobOf(openVersion);
  const rendition = useQuery(renditionQueryOptions(projectId, blobRefId));
  const build = useRequestRendition(projectId, blobRefId);

  /**
   * The authenticated door onto the tile pipeline, handed to the canvas rather than assumed by
   * it (`KLQ-20260826-002` §D gave the same pipeline a second door and the canvas no longer
   * knows which one it is behind).
   *
   * Memoised because the canvas rebuilds its viewer when this changes, and a fresh closure per
   * render would rebuild it per render. The rejection is unreachable from the tree below —
   * nothing renders the canvas with no blob — and is here because a hook cannot be conditional.
   */
  const signRendition = useCallback(
    (rect: TileRect): Promise<TileBatch> =>
      blobRefId === null
        ? Promise.reject(new Error("no rendition blob"))
        : signTiles(blobRefId, rect),
    [blobRefId],
  );

  // Cached, shared with the People screen's key, one fetch per project. Names are reference
  // data on this screen: nothing here changes them and nothing needs them live.
  const members = useQuery(membersQueryOptions(projectId));
  const nameOf = (userId: string | null | undefined) =>
    members.data?.items.find((member) => member.user_id === userId)
      ?.display_name ?? null;

  const open = openVersion;
  const superseded = open !== null && !open.is_current;
  /**
   * Whether a pin may be placed on the sheet that is actually on screen.
   *
   * **Authority is not enough, and the missing half is the version.** `create_pin` sets
   * `source_version_id` from the *slot's current version*, read server-side at the moment of
   * the write (`annotations/service.py`) — it does not take one, and there is no field in
   * which to offer it. So a pin placed while r03 is open would be recorded as a point on r04,
   * read off r03's geometry: a coordinate from one sheet stored as a coordinate on another,
   * with no badge to say so, and then dropped from `markers` below for being ahead of the
   * version being viewed. The person would watch their own pin vanish.
   *
   * Comment mode is therefore **absent** on a superseded version rather than disabled — the
   * banner above already says a newer version exists and offers to open it, which is the
   * action, and a greyed-out switch beside it would be a second, wordless account of the same
   * fact. Same rule the Compare control and the Review segment are held to.
   */
  const canComment = canWrite && open?.is_current === true;
  /**
   * Whether the tool rail is on screen — Comment mode on a sheet the caller may mark. Absent
   * otherwise, never disabled (issue #71, step 7).
   *
   * **The rail's switches take effect only while the rail is there.** A layer hidden in Comment
   * mode is shown again in View mode, and a tool armed there is not armed here: a person who
   * hid the pins and left Comment mode would otherwise be looking at a sheet with its pins gone
   * and no control anywhere on screen that explains it or brings them back.
   */
  const railShown = mode === "comment" && canComment;
  const pinsShown = !railShown || layers.pins;
  const markupShown = !railShown || layers.markup;
  const taskPinsShown = !railShown || layers.tasks;
  /** The tool a gesture actually reaches. A hidden Markup layer draws nothing, so arms nothing. */
  const armedTool = railShown && layers.markup ? tool : null;
  /**
   * The counter of the version on screen. Carry-forward is measured against this and nothing
   * else: a pin whose own counter is *behind* it is carried forward and badged; one *ahead* of
   * it was placed on a sheet this one has not become yet.
   */
  const openCounter = open?.counter ?? null;
  /**
   * How many pages the open version's sheet has — `null` until the rendition has said.
   *
   * Every page decision on this screen reads this one number: which page controls exist, which
   * page a fly-to may turn to, and which pins are on a page this version actually has. It is
   * `null` rather than `1` while unknown, because *"the sheet has one page"* and *"nobody has
   * told us yet"* are different answers and only the first of them may drop a pin.
   */
  const pageCount = rendition.data?.page_count ?? null;

  // --- Pins ------------------------------------------------------------------------
  //
  // One call for the whole drawing, whichever version is open (`api.listPins`). The thread
  // beside it is C1a's query read a second time rather than fetched a second time — the rail
  // below is already mounting `CommentThread` on that exact key, so this is a cache read whose
  // only job is mapping a pin back to the card that belongs to it.

  const pinList = usePins(projectId, slotId);
  const thread = useComments(projectId, slotId);
  const pinRows = useMemo(() => pinList.data?.items ?? [], [pinList.data]);

  const [transform, setTransform] = useState<SheetTransform | null>(null);
  const [selectedPinId, setSelectedPinId] = useState<string | null>(null);
  // The pin the screen still owes a fly-to. Held rather than acted on immediately, because the
  // pin may be on another page — or on a version whose canvas has not opened yet.
  const [pendingPinId, setPendingPinId] = useState<string | null>(null);
  // Three states, not two: `undefined` is *nobody has pressed a pin yet*, and only then does
  // the deep link's card stay highlighted. `null` is a pin that carries no comment having been
  // pressed — an answer, and one that has to outrank `?comment=`, or clicking an empty pin
  // would scroll the rail back to whichever card the link arrived on.
  const [pickedCommentId, setPickedCommentId] = useState<
    string | null | undefined
  >(undefined);
  const highlightId =
    pickedCommentId === undefined ? highlightCommentId : pickedCommentId;

  /**
   * The markers, and with them the numbering both surfaces use.
   *
   * `created_at` order, which is the order the server already returns — so ① is the oldest
   * topic on the sheet, on the sheet and in the rail, and the two cannot disagree because
   * neither of them numbers anything: this list is the only place a number is decided. A pin
   * with no comment still takes one (it is a place on the drawing somebody marked); a loose
   * comment takes none (it is about the drawing, not about a point on it).
   *
   * **A number is a promise that ① is somewhere on this sheet**, so everything this screen
   * cannot actually draw is dropped here rather than downstream — one filter, one numbering,
   * and no card offering to fly to a marker nobody drew. Three things fail that promise, and
   * `listPins` returns all three because it addresses the *slot*: an anchor this screen cannot
   * place, a pin from a version the open one has not become yet, and a pin on a page the open
   * version does not have.
   */
  const markers = useMemo<readonly PinMarker[]>(() => {
    let number = 0;
    return pinRows.flatMap((pin) => {
      // Read through `anchor_type`: an `element` anchor is a reserved seam and this screen
      // cannot place it, which is a marker it does not draw rather than one it draws wrong.
      const anchor = sheetAnchor(pin);
      if (anchor === null) return [];

      // **Carry-forward is forward-only.** `MCK-20260521-006` carries a pin from a *previous*
      // version onto a later one, and chat1.md §578 badges exactly that — *"Pin from r02"*.
      // Nothing carries backwards, so a pin placed on r04 is not on r01's sheet at all. Left
      // in, it would draw with no badge (the badge condition is `from < openCounter`) and be
      // indistinguishable from a pin somebody put there when r01 was current.
      const from = pin.source_version_counter ?? null;
      if (from !== null && openCounter !== null && from > openCounter)
        return [];

      // **The page has to exist on the open version.** A pin carried forward from a longer
      // r03 can name page 3 while r04 has two, and `ViewerCanvas` keys its geometry off
      // `pages[page]` — so turning to it renders `canvasUnavailable`, and on a single-page
      // rendition the page controls are not drawn at all, leaving no way back off the blank
      // sheet. Dropped rather than clamped: a pin does not belong on a page it was not placed
      // on, and moving it there would be drawing it wrong rather than not drawing it.
      if (pageCount === null || anchor.page < 0 || anchor.page >= pageCount)
        return [];

      number += 1;
      return [
        { id: pin.id, anchor, number, resolved: pin.resolved_at != null },
      ];
    });
  }, [pinRows, openCounter, pageCount]);

  // --- Task pins ----------------------------------------------------------------------
  //
  // The drawing's own task list, which the slot detail and the publish flow already read — one
  // cache entry, swept by every task mutation. A status changed anywhere in this tab reaches
  // the sheet on that sweep; nothing here polls, and the thing that would make somebody else's
  // change immediate is the push channel rather than a timer (CLAUDE.md, data fetching).

  const slotTasks = useSlotTasks(projectId, slotId);
  const navigate = useNavigate();

  /**
   * A task pin opens the task, on its own route (C8) rather than in a panel here.
   *
   * The viewer's rail is the comment thread's and stays the comment thread's: a second rail
   * for tasks would make *"which of these two lists am I looking at"* a question, and the task
   * detail already exists, is linkable, and is where a person edits one.
   */
  const openTask = useCallback(
    (taskId: string) => {
      void navigate({
        to: "/projects/$projectId/tasks/$taskId",
        params: { projectId, taskId },
      });
    },
    [navigate, projectId],
  );

  /**
   * Every task with a pin, on the page its anchor names.
   *
   * **The page comes from the anchor** (2026-09-25). Until then `TaskAnchor` was `(x, y)` and
   * this built every marker at `page: 0`, so a task placed on page 3 was drawn on page 1 —
   * a pin in a place nothing else in the product agreed with. `TaskPinLayer` had always
   * compared the marker's page with the transform's; it was this line that never had one.
   *
   * Unlike a comment pin there is no carry-forward: a task is owed by the *drawing*, not by
   * the version it was noticed on, so its pin is on every version's sheet until somebody
   * closes it.
   */
  const taskMarkers = useMemo<readonly TaskPinMarker[]>(() => {
    if (pageCount === null || pageCount < 1) return [];
    return (slotTasks.data ?? []).flatMap((task) => {
      if (task.anchor == null) return [];
      return [
        {
          id: task.id,
          anchor: {
            page: task.anchor.page,
            x: task.anchor.x,
            y: task.anchor.y,
          },
          title: task.title,
          settled: task.status === "done" || task.status === "closed",
        },
      ];
    });
  }, [slotTasks.data, pageCount]);

  /**
   * The same numbers, plus the carry-forward badge, as the rail needs them.
   *
   * Keyed off `markers` rather than off the pin list, so a card can only be numbered when a
   * marker of that number is on the sheet to be flown to. The badge is
   * `source_version_counter` against the counter being viewed and that is the whole of it —
   * no version is fetched per pin, which is exactly why the server carries the counter.
   */
  const annotations = useMemo<PinAnnotations>(() => {
    const counterOf = new Map(
      pinRows.map((pin) => [pin.id, pin.source_version_counter ?? null]),
    );
    return new Map(
      markers.map((marker) => {
        const from = counterOf.get(marker.id) ?? null;
        return [
          marker.id,
          {
            number: marker.number,
            carriedFrom:
              from !== null && openCounter !== null && from < openCounter
                ? from
                : null,
          },
        ];
      }),
    );
  }, [markers, pinRows, openCounter]);

  // The same stranding, reached from the other side: reading page 4 of a five-page r03 and
  // then opening a one-page r04 leaves `page` outside the new rendition, and a rendition with
  // one page renders no page controls to walk back with. The sheet decides which pages exist,
  // so the page being read follows it down.
  useEffect(() => {
    if (pageCount === null) return;
    setPage((current) => Math.min(current, pageCount - 1));
  }, [pageCount]);

  // The fly-to, run whenever the screen is finally able to. Turning the page first and
  // centring on the next pass is what makes a pin on page 2 reachable from a card in the rail
  // without the rail knowing pages exist.
  //
  // A pin still pending when its marker is gone is left pending on purpose: resolving a
  // `?comment=` asks for a fly-to *and* a version change in the same pass, and for the frame
  // between them the marker is legitimately absent from a sheet it is about to be on.
  useEffect(() => {
    if (pendingPinId === null || transform === null) return;
    const marker = markers.find((candidate) => candidate.id === pendingPinId);
    if (marker === undefined) return;
    if (marker.anchor.page !== transform.page) {
      // Retro rule 9: `markers` has already dropped every anchor outside this rendition, and
      // this is that property enforced where it is acted on rather than trusted. `setPage`
      // past `page_count` is the one move that can strand the screen on a blank sheet.
      if (pageCount !== null && marker.anchor.page < pageCount)
        setPage(marker.anchor.page);
      return;
    }
    transform.centreOn(marker.anchor);
    setPendingPinId(null);
  }, [pendingPinId, transform, markers, pageCount]);

  // --- Placing one ------------------------------------------------------------------
  //
  // A press on the sheet chooses a point; the composer that appears over it carries the words;
  // one POST writes the pin and its first comment together. Nothing is written before the
  // send, so an abandoned pin leaves no row anywhere — which is the whole reason
  // `PinCreateRequest` takes a `body`.

  const [pending, setPending] = useState<SheetAnchor | null>(null);
  // The refusal, held here rather than read off the mutation, so that abandoning a pin clears
  // it with a plain state setter — and so a failure from a pin placed a minute ago cannot
  // reappear under the next one.
  const [placeError, setPlaceError] = useState<unknown>(null);
  const create = useCreatePin(projectId, slotId);

  /** Abandon the pending pin and everything the last attempt said about it. */
  const cancelPin = useCallback(() => {
    setPending(null);
    setPlaceError(null);
  }, []);

  useEffect(() => {
    // Placing is offered while Comment mode is on and nothing is pending: a second press
    // should not move a pin somebody is already writing about, and the composer's Cancel is
    // the way to choose a different spot.
    //
    // An armed markup tool, or hidden pins, take the press away from pins altogether: a tap
    // meant to start a stroke must not also drop a pin (`overlay.ts`, `subscribeDrag`'s note
    // on who owns that decision — this screen, which is who arms the tool).
    if (
      transform === null ||
      mode !== "comment" ||
      !canComment ||
      pending !== null ||
      armedTool !== null ||
      !pinsShown
    ) {
      return undefined;
    }
    return transform.subscribePress((point) => {
      // The page comes from the transform rather than from `page`, because the transform is
      // the mapping the coordinate was read through — E3 exists so that a pin placed on page 2
      // is stored as page 2, and taking the number from anywhere else is how the two drift.
      setPending({ ...point, page: transform.page });
    });
  }, [transform, mode, canComment, pending, armedTool, pinsShown]);

  /**
   * A pending pin does not survive the sheet under it changing.
   *
   * Turning the page or opening another version leaves the composer anchored to a point that
   * is no longer on screen — `PinLayer` hides a marker whose page is not the transform's, and
   * the composer goes with it. What is left is Comment mode with an invisible, unfocusable
   * panel holding text, no way to reach its Cancel, and every further press ignored because
   * something is already pending. Clearing it is the same thing leaving Comment mode does.
   */
  const openId = open?.id ?? null;

  // --- Markup (issue #71) --------------------------------------------------------------
  //
  // Keyed on the open version: a stroke drawn on r03 is r03's and is not drawn on r04
  // (`queries.ts`, `markupsQueryOptions`). Fetched whichever way the Markup switch is set.

  const markupList = useMarkups(projectId, openId);
  const markupRows = useMemo(
    () => markupList.data?.items ?? [],
    [markupList.data],
  );
  const drawMarkup = useCreateMarkup(projectId, openId);
  // Held here rather than read off the mutation, for `placeError`'s reason: a failure from a
  // stroke a minute ago must not reappear over the next one.
  const [markupError, setMarkupError] = useState<unknown>(null);
  /** The text stroke being written: placed by a press, worded by the composer, not yet sent. */
  const [textDraft, setTextDraft] = useState<TextGeometry | null>(null);

  /**
   * The colour a stroke in progress is previewed in. Resolved from the token the same way the
   * write resolves it, so the preview and the row that lands cannot be two different colours.
   */
  const strokeColor = useMemo(() => resolveColor(color), [color]);

  const cancelText = useCallback(() => {
    setTextDraft(null);
    setMarkupError(null);
  }, []);

  /**
   * Post one finished stroke. **Nothing is optimistic and nothing is retried** — the endpoint
   * takes no `Idempotency-Key`, so a retry is a second row, and a stroke drawn before the server
   * took it would be a stroke this screen cannot take back if it refuses (`queries.ts`).
   *
   * The hex is produced here, at the moment of the write, from the token's name
   * (`markup.ts:resolveColor`). `geometry_json` carries `kind` beside the shape: the server takes
   * any object, and `geometryOf` reads the row's own `kind` column rather than this copy.
   *
   * **`mutateAsync`, not `mutate` with per-call callbacks.** TanStack Query fires a per-call
   * `onSuccess`/`onSettled` only for the *latest* call, so two strokes let go of in quick
   * succession left the first one's in-flight copy on the sheet for good, drawn over its own
   * row. Each call's promise settles for that call. It resolves after the hook's own `onSuccess`
   * has put the row in the cache, which is what keeps the handover without a frame of neither.
   * Answers whether the server took the stroke; a refusal is shown, never thrown.
   */
  const { mutateAsync: postMarkup } = drawMarkup;
  const sendStroke = useCallback(
    async (geometry: MarkupGeometry): Promise<boolean> => {
      setMarkupError(null);
      try {
        await postMarkup({
          kind: geometry.kind,
          color: resolveColor(color),
          layer: "markup",
          geometry_json: { ...geometry },
        });
        return true;
      } catch (error) {
        setMarkupError(error);
        return false;
      }
    },
    [postMarkup, color],
  );

  /**
   * Strokes the pointer has let go of and the server has not answered for yet.
   *
   * **Drawn, not optimistic.** Without this the line vanished the moment the button came up and
   * reappeared a round trip later, which read as the stroke having failed. It stays on the sheet
   * exactly as drawn until its row is in the cache (`useCreateMarkup` appends it in the same
   * tick this entry leaves) — and a refused write removes it, with the error beside it, so a
   * stroke the server never took is never left looking like one it did. Keyed on the version
   * it was drawn on, because switching versions mid-flight must not paint it onto the next one.
   */
  const [sendingStrokes, setSendingStrokes] = useState<
    readonly SendingStroke[]
  >([]);
  const sendingSeq = useRef(0);

  const commitStroke = useCallback(
    (geometry: MarkupGeometry) => {
      if (openId === null) return;
      sendingSeq.current += 1;
      const key = `sending:${String(sendingSeq.current)}`;
      setSendingStrokes((current) => [
        ...current,
        { key, geometry, color: strokeColor, versionId: openId },
      ]);
      void sendStroke(geometry).finally(() => {
        setSendingStrokes((current) =>
          current.filter((one) => one.key !== key),
        );
      });
    },
    [sendStroke, strokeColor, openId],
  );

  const sendingOnOpen = useMemo(
    () => sendingStrokes.filter((one) => one.versionId === openId),
    [sendingStrokes, openId],
  );

  // --- Eraser, Clear and Undo (owner, 2026-09-17) -------------------------------------
  //
  // They reach only what this person drew in this session (`markupSession.ts`), and every
  // removal is a real `DELETE` — a stroke taken back is gone for everybody, not hidden here.

  const session = useMarkupSession();
  const { mutateAsync: removeMarkup } = useDeleteMarkup(projectId, openId);
  const { mutateAsync: restoreMarkups } = useRestoreMarkups(projectId, openId);
  /**
   * Strokes on their way out, off the sheet from the moment the eraser touches them — the
   * mirror of `sendingStrokes`: an eraser that waited a round trip to show it worked would read
   * as one that did not. A refused removal puts the stroke back, with the error beside it.
   *
   * The ref is the same set, written synchronously: one swipe reports the same stroke on
   * several steps before React has rendered once, and a stroke sent twice would come back
   * twice on Undo.
   */
  const [erasing, setErasing] = useState<ReadonlySet<string>>(() => new Set());
  const erasingNow = useRef(new Set<string>());
  const [undoing, setUndoing] = useState(false);

  const visibleMarkups = useMemo(
    () => markupRows.filter((row) => !erasing.has(row.id)),
    [markupRows, erasing],
  );
  const erasableIds = useMemo(
    () =>
      new Set(
        visibleMarkups
          .filter((row) => session.drawn.has(row.id))
          .map((row) => row.id),
      ),
    [visibleMarkups, session.drawn],
  );

  /** Remove rows; answers the ones that are gone. `404` counts as gone (`useDeleteMarkup`). */
  const takeBack = useCallback(
    async (candidates: readonly Markup[]): Promise<readonly Markup[]> => {
      const rows = candidates.filter((row) => !erasingNow.current.has(row.id));
      if (rows.length === 0) return [];
      for (const row of rows) erasingNow.current.add(row.id);
      setMarkupError(null);
      setErasing(new Set(erasingNow.current));
      const settled = await Promise.allSettled(
        rows.map((row) => removeMarkup(row.id)),
      );
      for (const row of rows) erasingNow.current.delete(row.id);
      setErasing(new Set(erasingNow.current));
      const refused = settled.find((one) => one.status === "rejected");
      if (refused !== undefined) setMarkupError(refused.reason);
      return rows.filter(
        (_row, index) => settled[index]?.status === "fulfilled",
      );
    },
    [removeMarkup],
  );

  /** The eraser passed over these, or Clear asked for all of them: one act for Undo. */
  const eraseStrokes = useCallback(
    (ids: readonly string[]) => {
      if (openId === null) return;
      const version = openId;
      const rows = markupRows.filter(
        (row) => ids.includes(row.id) && session.drawn.has(row.id),
      );
      void takeBack(rows).then((gone) => {
        recordErased(version, gone);
      });
    },
    [openId, markupRows, session.drawn, takeBack],
  );

  const clearMine = useCallback(() => {
    eraseStrokes([...erasableIds]);
  }, [eraseStrokes, erasableIds]);

  /**
   * Take back the newest act on the open version: a drawn stroke is removed, an erase is drawn
   * again. Offered only while nothing is in flight — with a stroke still sending, Undo would
   * take back the one before it, which is not what the person pressing it means.
   */
  const undo = useCallback(() => {
    if (openId === null) return;
    const entry = popEntry(openId);
    if (entry === null) return;
    setMarkupError(null);
    setUndoing(true);
    const done = () => {
      setUndoing(false);
    };
    if (entry.type === "erased") {
      void restoreMarkups(entry.rows).catch(setMarkupError).finally(done);
      return;
    }
    const row = markupRows.find((one) => one.id === entry.id);
    if (row === undefined) {
      // Already gone — erased from another tab. Nothing left to take back.
      forgetDrawn(entry.id);
      done();
      return;
    }
    void takeBack([row])
      .then((gone) => {
        if (gone.length > 0) forgetDrawn(entry.id);
      })
      .finally(done);
  }, [openId, markupRows, restoreMarkups, takeBack]);

  const canUndo =
    openId !== null &&
    (session.history.get(openId)?.length ?? 0) > 0 &&
    !undoing &&
    erasing.size === 0 &&
    sendingOnOpen.length === 0;

  const placeText = useCallback(() => {
    if (textDraft === null) return;
    const value = textDraft.value.trim();
    if (value === "") return;
    // Cleared only once the row is back: `useCreateMarkup` has appended it to the cache by then,
    // so the draft hands over to the committed stroke without a frame of neither.
    void sendStroke({ ...textDraft, value }).then((sent) => {
      if (sent) setTextDraft(null);
    });
  }, [textDraft, sendStroke]);

  // The text tool's placement is a press, not a drag (`MarkupLayer`). One draft at a time — a
  // second press does not move words somebody is typing; Cancel is how to choose another spot.
  useEffect(() => {
    if (transform === null || armedTool !== "text" || textDraft !== null)
      return undefined;
    return transform.subscribePress((point) => {
      setMarkupError(null);
      setTextDraft({
        kind: "text",
        page: transform.page,
        at: point,
        value: "",
      });
    });
  }, [transform, armedTool, textDraft]);

  /** Pick a tool on the rail. Pin is `null`. Whatever was half-placed under the old one goes. */
  const chooseTool = useCallback(
    (next: MarkupTool | null) => {
      cancelPin();
      cancelText();
      setTool(next);
      // Choosing a tool whose layer is hidden shows that layer: drawing a stroke nobody can
      // see, or pressing for a pin that is not drawn, is a gesture with no visible outcome.
      setLayers((current) =>
        next === null
          ? { ...current, pins: true }
          : { ...current, markup: true },
      );
    },
    [cancelPin, cancelText],
  );

  /** Flip a layer. Hiding one abandons whatever was half-placed on it. */
  const changeLayers = useCallback(
    (next: ViewerLayers) => {
      if (!next.pins) cancelPin();
      if (!next.markup) {
        cancelText();
        setTool(null);
      }
      setLayers(next);
    },
    [cancelPin, cancelText],
  );

  useEffect(() => {
    cancelPin();
    cancelText();
  }, [page, openId, cancelPin, cancelText]);

  /**
   * The one request: the pin, and the first thing said about it.
   *
   * The pending marker is cleared only on success — a refused write keeps both the point and
   * the words, because retyping a comment is the cost of a `409` nobody caused.
   */
  const sendPin = useCallback(
    (body: string) => {
      if (pending === null) return;
      setPlaceError(null);
      create.mutate(
        { anchor: pending, body },
        {
          onSuccess: () => {
            setPending(null);
          },
          onError: setPlaceError,
        },
      );
    },
    [pending, create],
  );

  /** Card → pin. The rail asks for a point on the sheet and the canvas flies to it. */
  const selectPinFromCard = useCallback((pinId: string) => {
    setSelectedPinId(pinId);
    setPendingPinId(pinId);
  }, []);

  /**
   * Pin → card. The sheet names a topic and the rail scrolls to what was said about it.
   *
   * The rail is switched to the conversation first: the card only exists while that tab is
   * mounted, so pressing a pin with Versions open would otherwise select the marker and do
   * nothing else — the half of the criterion that is invisible until somebody tries it.
   */
  const selectPinFromSheet = useCallback(
    (pinId: string) => {
      setSelectedPinId(pinId);
      setTab("comments");
      const first =
        thread.data?.items.find((row) => row.pin_id === pinId) ?? null;
      setPickedCommentId(first?.id ?? null);
    },
    [thread.data],
  );

  /**
   * `?comment=` on a pinned comment, resolved **once per comment id**.
   *
   * The link names a comment; the pin under it names a version. When `?rev=` is absent the
   * address has not chosen one, so the pin's own sheet wins — `replace`, because arriving is
   * not a navigation. When `?rev=` *is* present the caller named a version deliberately and
   * nothing here overrules it.
   *
   * An unknown comment id, or one outside this drawing, resolves to nothing and renders what
   * an unknown id already rendered: no highlight, no fly-to. The two cases are byte-identical
   * server-side (`MCK-20260812-006` C) and this screen does not undo that by telling them
   * apart.
   *
   * **The ref holds the id it resolved, not a flag that it did.** A second `?comment=` can
   * arrive on a route that never unmounted — a search-param navigation rather than a fresh
   * load, which is exactly what one link inside this screen to another comment on the same
   * drawing is. A latched boolean would swallow every link after the first: no fly-to, no
   * version resolution, and nothing on screen to distinguish it from a dead id. It also
   * un-picks a pin pressed since: a freshly named comment outranks the one the last click on
   * the sheet chose, or the rail would highlight a card the address stopped naming.
   */
  const deepLinkResolved = useRef<string | null>(null);
  useEffect(() => {
    if (
      highlightCommentId === null ||
      deepLinkResolved.current === highlightCommentId
    )
      return;
    if (open === null) return;
    const comment = thread.data?.items.find(
      (row) => row.id === highlightCommentId,
    );
    if (comment === undefined) return;
    if (comment.pin_id == null) {
      deepLinkResolved.current = highlightCommentId;
      setPickedCommentId(undefined);
      return;
    }
    const pin = pinRows.find((row) => row.id === comment.pin_id);
    if (pin === undefined) return;
    deepLinkResolved.current = highlightCommentId;
    setPickedCommentId(undefined);
    setSelectedPinId(pin.id);
    setPendingPinId(pin.id);
    if (versionId === null && pin.source_version_id !== open.id) {
      onOpenVersion(pin.source_version_id, {
        replace: true,
        keepHighlight: true,
      });
    }
  }, [
    highlightCommentId,
    thread.data,
    pinRows,
    open,
    versionId,
    onOpenVersion,
  ]);

  /**
   * Fetch a short-lived signed URL and let the browser follow it.
   *
   * Not a synthesised `<a download>`: the URL carries a `Content-Disposition` the *server*
   * chose from the sanitised filename, and following it keeps that decision where the
   * sanitising happened. The API never sees a byte of this (ADR-0005) — it issues the token
   * and steps out of the way.
   */
  const download = async (blobRefId: string) => {
    const ticket = await readDownloadUrl(blobRefId);
    window.open(ticket.url, "_blank", "noopener,noreferrer");
  };

  if (!belongsToSlot || (history.isSuccess && versions.length === 0)) {
    return (
      <div className={styles.screen}>
        <div className={styles.state}>
          <h2 className={styles.stateTitle}>{t("viewer.missing.title")}</h2>
          <p className={styles.stateBody}>{t("viewer.missing.body")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.screen}>
      <div className={styles.topBar}>
        {/* The exit. `router.tsx` nests this route under `/drawings` *because of* this link —
            "a real parent rather than a guess" — and for one wave the justification existed
            with nothing enforcing it (retro rule 9), leaving the Drawings tab as the only way
            out of the screen the product exists for. A `Link` and not `history.back()`: it has
            to work for somebody who arrived by a pasted URL, which is how most people reach a
            drawing. The search params are dropped deliberately — the tree opens where it left
            off, and `?slot=` here would be this sheet rather than where they were reading. */}
        <Link
          to="/projects/$projectId/drawings"
          params={{ projectId }}
          search={{}}
          className={styles.backLink}
        >
          <ChevronLeft size={16} strokeWidth={1.5} aria-hidden />
          {/* The words go to the ear alone on a phone (`Viewer.module.css`, the 767px block):
              56px of bar has no room for them, and a chevron at the top left of a full-screen
              sheet is not ambiguous. */}
          <span className={styles.backLinkLabel}>
            {t("viewer.backToDrawings")}
          </span>
        </Link>
        <span className={styles.identifier}>{open?.identifier ?? "—"}</span>
        {/* A native select under `components/Select`, not a custom dropdown: it is a list of
            one drawing's versions, it must work under a thumb on site, and the platform's own
            picker does that better than anything this screen would build. The label is hidden
            because the identifier beside it already says which drawing these belong to. */}
        <div className={styles.versionPicker}>
          <Select
            label={t("viewer.versionPicker")}
            labelHidden
            size="sm"
            value={open?.id ?? ""}
            onChange={(event) => {
              const next = event.target.value;
              onOpenVersion(next === currentVersion?.id ? null : next);
            }}
          >
            {versions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.is_current
                  ? t("viewer.optionCurrent", { name: item.identifier })
                  : item.identifier}
              </option>
            ))}
          </Select>
        </div>
        {open?.is_current === true ? (
          <Pill tone="success">{t("viewer.current")}</Pill>
        ) : null}
        <span className={styles.spacer} />
        {/* Wireframe 2's mode switcher, with two segments rather than three: Review belongs to
            `MCK-20260521-007` and none of it exists. Absent — not disabled — for anybody who
            cannot place a pin on the sheet in front of them, which covers both a caller with
            read access and a superseded version (`canComment`). */}
        {canComment ? (
          <div
            className={styles.modes}
            role="group"
            aria-label={t("viewer.mode.label")}
          >
            {(["view", "comment"] as const).map((segment) => (
              <button
                key={segment}
                type="button"
                className={[
                  styles.mode,
                  mode === segment ? styles.modeActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-pressed={mode === segment}
                onClick={() => {
                  // Leaving Comment mode abandons a pin nobody has finished writing about.
                  // Nothing was written, so there is nothing to undo — but a composer left
                  // over a sheet that no longer takes presses is a dead panel.
                  // The same goes for a text stroke nobody has placed, and the armed tool: View
                  // mode has no rail, and a tool armed out of sight would come back armed.
                  if (segment === "view") {
                    cancelPin();
                    cancelText();
                    setTool(null);
                  }
                  setMode(segment);
                }}
              >
                {t(
                  segment === "view"
                    ? "viewer.mode.view"
                    : "viewer.mode.comment",
                )}
              </button>
            ))}
          </div>
        ) : null}
        {rendition.data?.truncated === true ? (
          <Pill tone="info">{t("viewer.truncated")}</Pill>
        ) : null}
      </div>

      {/* Derived from a list this screen already holds, refetched on focus. No subscription,
          no polling loop — Approach §6, and the surviving online half of MCK-20260521-006's
          deferred offline criterion. */}
      {superseded && currentVersion !== null ? (
        <div className={styles.superseded}>
          <span>
            {t("viewer.superseded", { name: currentVersion.identifier })}
          </span>
          <button
            type="button"
            className={styles.control}
            onClick={() => {
              onOpenVersion(null);
            }}
          >
            {t("viewer.openCurrent")}
          </button>
        </div>
      ) : null}

      <div
        className={[styles.body, railShown ? styles.bodyWithTools : ""]
          .filter(Boolean)
          .join(" ")}
      >
        {railShown ? (
          // The dock is `display: contents` above 768px, so the rail stays the grid column it
          // has always been and this wrapper costs nothing. On a phone it becomes a drawer over
          // the sheet with a handle on its edge — the handle travels with it, so the control
          // that closes it is where the thing it closed is.
          <div
            className={[
              styles.toolDock,
              phoneToolsOpen ? styles.toolDockOpen : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <ToolRail
              tool={tool}
              color={color}
              layers={layers}
              onTool={chooseTool}
              onColor={setColor}
              onLayers={changeLayers}
              canUndo={canUndo}
              canClear={erasableIds.size > 0}
              onUndo={undo}
              onClear={clearMine}
            />
            <button
              type="button"
              className={styles.toolDockHandle}
              aria-expanded={phoneToolsOpen}
              aria-label={t(
                phoneToolsOpen ? "markup.rail.hide" : "markup.rail.show",
              )}
              onClick={() => {
                setPhoneToolsOpen(!phoneToolsOpen);
              }}
            >
              {phoneToolsOpen ? (
                <ChevronLeft size={18} strokeWidth={1.5} aria-hidden />
              ) : (
                <ChevronRight size={18} strokeWidth={1.5} aria-hidden />
              )}
            </button>
          </div>
        ) : null}
        {/* The crosshair belongs to the stage rather than to the pin layer: the layer is
            `pointer-events: none` so a pan stays a pan, and an element the pointer never hits
            cannot set a cursor. */}
        <div
          className={[
            styles.stage,
            mode === "comment" && canComment ? styles.stagePlacing : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {rendition.data === undefined ? null : rendition.data.state ===
              "ready" && blobRefId !== null ? (
            <>
              {/* No fallback element: the stage is already ink-coloured and the canvas paints
                  its blurred placeholder the moment it arrives. A spinner here would be the
                  one thing §4 rules out, one layer above where it rules it out. */}
              <Suspense fallback={null}>
                <ViewerCanvas
                  rendition={rendition.data}
                  renditionId={rendition.data.rendition_id ?? null}
                  sign={signRendition}
                  page={page}
                  onTransform={setTransform}
                />
              </Suspense>
              {/* Outside the `Suspense` boundary and after the canvas in the DOM, so the
                  markers sit over the sheet without being torn down with it. `transform` is
                  null until the canvas opens, and the layer renders nothing until it is not. */}
              {/* Markup under the pins: a pin is a control and a stroke is ink, so the thing a
                  person presses stays on top of the thing they drew. Mounted or not by the
                  layer switch; the query behind it is untouched either way. */}
              {markupShown ? (
                <MarkupLayer
                  transform={transform}
                  markups={visibleMarkups}
                  tool={armedTool}
                  color={strokeColor}
                  sending={sendingOnOpen}
                  draft={armedTool === "text" ? textDraft : null}
                  onCommit={commitStroke}
                  erasable={erasableIds}
                  onErase={eraseStrokes}
                />
              ) : null}
              {pinsShown ? (
                <PinLayer
                  transform={transform}
                  markers={markers}
                  selectedPinId={selectedPinId}
                  onSelect={selectPinFromSheet}
                  pending={pending}
                >
                  {pending === null ? null : (
                    <PinComposer
                      sending={create.isPending}
                      error={placeError}
                      onSend={sendPin}
                      onCancel={cancelPin}
                    />
                  )}
                </PinLayer>
              ) : null}
              {/* Over the comment pins, because a task is the thing somebody came to the sheet
                  to act on — and because the two are told apart by colour, a task pin behind a
                  comment pin would read as a colour nobody can see. */}
              {taskPinsShown ? (
                <TaskPinLayer
                  transform={transform}
                  markers={taskMarkers}
                  selectedTaskId={null}
                  onSelect={openTask}
                />
              ) : null}
              {armedTool === "text" && textDraft !== null ? (
                <MarkupTextComposer
                  value={textDraft.value}
                  sending={drawMarkup.isPending}
                  error={markupError}
                  onChange={(value) => {
                    setTextDraft({ ...textDraft, value });
                  }}
                  onPlace={placeText}
                  onCancel={cancelText}
                />
              ) : markupError == null ? null : (
                <div className={styles.markupError} role="alert">
                  <InlineError variant="inline">
                    {t(apiErrorKey(markupError))}
                  </InlineError>
                </div>
              )}
              <div className={styles.controls}>
                {pageCount !== null && pageCount > 1 ? (
                  <>
                    <button
                      type="button"
                      className={styles.control}
                      aria-label={t("viewer.previousPage")}
                      disabled={page === 0}
                      onClick={() => {
                        setPage((current) => Math.max(current - 1, 0));
                      }}
                    >
                      <ChevronLeft size={16} strokeWidth={1.5} aria-hidden />
                    </button>
                    <span className={styles.pageIndicator}>
                      {t("viewer.pageOf", { page: page + 1, count: pageCount })}
                    </span>
                    <button
                      type="button"
                      className={styles.control}
                      aria-label={t("viewer.nextPage")}
                      disabled={page >= pageCount - 1}
                      onClick={() => {
                        setPage((current) =>
                          Math.min(current + 1, pageCount - 1),
                        );
                      }}
                    >
                      <ChevronRight size={16} strokeWidth={1.5} aria-hidden />
                    </button>
                  </>
                ) : null}
              </div>
            </>
          ) : (
            <RenditionState
              // `ready` is handled by the branch above, and the union is narrowed here rather
              // than widened there: a state this component cannot draw must be a type error,
              // not a blank panel.
              state={
                rendition.data.state === "ready"
                  ? "failed"
                  : rendition.data.state
              }
              failureCode={rendition.data.failure_code}
              onBuild={
                rendition.data.state === "absent" ||
                rendition.data.state === "failed"
                  ? () => {
                      build.mutate();
                    }
                  : undefined
              }
              building={build.isPending}
            />
          )}
        </div>

        <div
          className={[styles.rail, phoneRailOpen ? styles.railOpen : ""]
            .filter(Boolean)
            .join(" ")}
        >
          <div className={styles.tabs}>
            <button
              type="button"
              className={[
                styles.tab,
                tab === "comments" ? styles.tabActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => {
                setTab("comments");
              }}
            >
              {t("thread.title")}
            </button>
            <button
              type="button"
              className={[
                styles.tab,
                tab === "versions" ? styles.tabActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => {
                setTab("versions");
              }}
            >
              {t("viewer.versions.title")}
            </button>
          </div>
          <div className={styles.railBody}>
            {tab === "comments" ? (
              // Written once in phase 1, mounted here for the second time without a change —
              // retro rule 8's cross-cutting object, and the reason the conversation shipped
              // before the canvas rather than behind it.
              <CommentThread
                projectId={projectId}
                slotId={slotId}
                canWrite={canWrite}
                highlightId={highlightId}
                pins={annotations}
                onSelectPin={selectPinFromCard}
                // Wireframe 2's empty state is Comment mode's — *"Tap on the drawing to place
                // a pin"* is only true while a tap on the drawing does that. In View mode, and
                // on a superseded version, the thread's own sentence stands.
                canPlacePin={mode === "comment" && canComment}
                controls
              />
            ) : (
              <>
                <VersionsRail
                  versions={versions}
                  openVersionId={open?.id ?? null}
                  onOpen={(id) => {
                    onOpenVersion(id === currentVersion?.id ? null : id);
                  }}
                  loading={history.isPending}
                />
                {open === null ? null : (
                  <>
                    <p className={styles.muted}>
                      {t("viewer.publishedBy", {
                        who:
                          nameOf(open.published_by_user_id) ??
                          t("viewer.unknownAuthor"),
                        when: relativeTime(open.published_at, locale),
                      })}
                    </p>
                    {/* The rendition and every source attachment, by the name its uploader
                        knew. This is the only place on the screen that touches the original
                        bytes, and it does so because a person asked. */}
                    <ul className={styles.downloads}>
                      {(open.files ?? []).map((file) => (
                        <li key={file.id} className={styles.download}>
                          <span className={styles.downloadKind}>
                            {file.kind}
                          </span>
                          <button
                            type="button"
                            className={styles.control}
                            onClick={() => {
                              void download(file.blob_ref_id);
                            }}
                          >
                            {file.original_filename ?? t("viewer.unnamedFile")}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Wireframe 5a's bottom action bar: 64px, fixed, in thumb reach, and drawn only under
          768px — the stylesheet decides that, not a hook. Three actions and not the wireframe's
          four: *Save offline* is absent because `MCK-20260521-011` is on hold and a control
          that lies about its state is worse than a missing one, which is the same reason the
          offline indicator is missing from the top bar. */}
      <nav className={styles.phoneBar} aria-label={t("viewer.phone.actions")}>
        {canComment ? (
          <button
            type="button"
            className={[
              styles.phoneAction,
              mode === "comment" ? styles.phoneActionOn : "",
            ]
              .filter(Boolean)
              .join(" ")}
            aria-pressed={mode === "comment"}
            onClick={() => {
              // The same abandon rule the desktop switcher holds to: leaving Comment mode
              // takes the half-written pin and the armed tool with it.
              if (mode === "comment") {
                cancelPin();
                cancelText();
                setTool(null);
                setMode("view");
                return;
              }
              setMode("comment");
              setPhoneRailOpen(false);
            }}
          >
            <MapPin size={20} strokeWidth={1.5} aria-hidden />
            <span className={styles.phoneActionLabel}>
              {t("viewer.mode.comment")}
            </span>
          </button>
        ) : null}
        <button
          type="button"
          className={[
            styles.phoneAction,
            phoneRailOpen && tab === "comments" ? styles.phoneActionOn : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-pressed={phoneRailOpen && tab === "comments"}
          onClick={() => {
            setTab("comments");
            setPhoneRailOpen(!(phoneRailOpen && tab === "comments"));
          }}
        >
          <MessageSquare size={20} strokeWidth={1.5} aria-hidden />
          <span className={styles.phoneActionLabel}>{t("thread.title")}</span>
        </button>
        <button
          type="button"
          className={[
            styles.phoneAction,
            phoneRailOpen && tab === "versions" ? styles.phoneActionOn : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-pressed={phoneRailOpen && tab === "versions"}
          onClick={() => {
            setTab("versions");
            setPhoneRailOpen(!(phoneRailOpen && tab === "versions"));
          }}
        >
          <History size={20} strokeWidth={1.5} aria-hidden />
          <span className={styles.phoneActionLabel}>
            {t("viewer.versions.title")}
          </span>
        </button>
      </nav>
    </div>
  );
}
